/**
 * CustomizedTreeView - Componente que exibe transportadoras agrupadas por status
 * 
 * Este componente processa dados reais de trips e os agrupa por transportadora,
 * mostrando quantas viagens estão em cada status. Permite filtrar os dados
 * clicando nos itens da árvore.
 * 
 * FUNCIONALIDADE DE FILTRO:
 * - Clicar em uma transportadora: filtra todas as viagens dessa transportadora
 * - Clicar em um status: filtra viagens dessa transportadora com esse status específico
 * - Clicar novamente no mesmo item: remove o filtro (toggle)
 * - Itens ativos recebem destaque visual
 * 
 * @example
 * // Exemplo de dados de entrada:
 * const trips = [
 *   { id: 1, carrier_name: 'Agregado', state: 'started', status: 'Em Rota Coletando' },
 *   { id: 2, carrier_name: 'Agregado', state: 'finished', status: 'Concluída' },
 *   { id: 3, carrier_name: 'JK', state: 'stopped', status: 'Quebrado' }
 * ];
 * 
 * // Resultado na árvore:
 * // ├── Agregado (2)
 * // │   ├── 🔵 Em Rota Coletando (1)
 * // │   └── 🟢 Concluída (1)
 * // └── JK (1)
 * //     └── 🔴 Quebrado (1)
 * 
 * // Uso com filtro:
 * const handleFilterChange = (filter) => {
 *   // filter = { type: 'carrier', carrierName: 'Agregado', label: 'Agregado' }
 *   // ou
 *   // filter = { type: 'status', carrierName: 'JK', statusKey: 'stopped', statusLabel: 'Quebrado', label: 'JK • Quebrado' }
 *   // ou
 *   // filter = null (remover filtro)
 * };
 * 
 * @param {Array} trips - Array de objetos trip com carrier_name, state e status
 * @param {Function} onFilterChange - Callback chamado quando filtro é alterado
 * @param {Object} activeFilter - Filtro ativo atual
 */
import { animated, useSpring } from '@react-spring/web';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import * as React from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import {
  TreeItemContent,
  TreeItemIconContainer,
  TreeItemLabel,
  TreeItemRoot,
} from '@mui/x-tree-view/TreeItem';
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon';
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider';
import { useTreeItem } from '@mui/x-tree-view/useTreeItem';

import { useTheme } from '@mui/material/styles';

// Mapeamento dos estados das trips para cores
const STATE_COLOR_MAP = {
  // Estados problemáticos - vermelho
  'stopped': 'red',
  'off_duty': 'red',
  // Estados operacionais - azul
  'on_duty': 'blue',
  'started': 'blue',
  'picture_received': 'blue',
  'estimated': 'blue',
  // Estados finalizados - verde
  'finished': 'green',
  // Estado pendente - amarelo
  'pending': 'yellow',
};

// Mapeamento dos estados para labels em português
const STATE_LABELS = {
  'pending': 'Pendente',
  'on_duty': 'Disponível',
  'off_duty': 'Indisponível',
  'started': 'Em Rota Coletando',
  'picture_received': 'Foto Recebida',
  'estimated': 'Retornando ao Hub',
  'finished': 'Concluída',
  'stopped': 'Quebrado',
};

// Função para processar as trips e agrupá-las por transportadora e status
function processTripsData(trips) {
  if (!trips || trips.length === 0) {
    return [];
  }

  // Agrupa trips por transportadora
  const groupedByCarrier = trips.reduce((acc, trip) => {
    const carrierName = trip.carrier_name || 'Sem Transportadora';
    if (!acc[carrierName]) {
      acc[carrierName] = [];
    }
    acc[carrierName].push(trip);
    return acc;
  }, {});

  // Converte para formato do tree view e ordena transportadoras alfabeticamente
  return Object.entries(groupedByCarrier)
    .sort(([carrierA], [carrierB]) => carrierA.localeCompare(carrierB))
    .map(([carrierName, carrierTrips], carrierIndex) => {
      // Agrupa trips por status dentro da transportadora
      const statusCounts = carrierTrips.reduce((acc, trip) => {
        const status = trip.state;
        if (!acc[status]) {
          acc[status] = 0;
        }
        acc[status] += 1;
        return acc;
      }, {});

      // Cria os itens filhos para cada status
      const children = Object.entries(statusCounts).map(([status, count], statusIndex) => ({
        id: `${carrierIndex + 1}.${statusIndex + 1}`,
        label: `${STATE_LABELS[status] || status} (${count})`,
        color: STATE_COLOR_MAP[status] || 'gray',
        count: count,
        status: status,
      }));

      return {
        id: String(carrierIndex + 1),
        label: `${carrierName} (${carrierTrips.length})`,
        children: children,
      };
    });
}

function DotIcon({ color }) {
  return (
    <Box sx={{ marginRight: 1, display: 'flex', alignItems: 'center' }}>
      <svg width={6} height={6}>
        <circle cx={3} cy={3} r={3} fill={color} />
      </svg>
    </Box>
  );
}

DotIcon.propTypes = {
  color: PropTypes.string.isRequired,
};

const AnimatedCollapse = animated(Collapse);

function TransitionComponent(props) {
  const style = useSpring({
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(0,${props.in ? 0 : 20}px,0)`,
    },
  });

  return <AnimatedCollapse style={style} {...props} />;
}

TransitionComponent.propTypes = {
  /**
   * Show the component; triggers the enter or exit states
   */
  in: PropTypes.bool,
};

function CustomLabel({ color, expandable, children, ...other }) {
  const theme = useTheme();
  const colors = {
    blue: (theme.vars || theme).palette.primary.main,
    green: (theme.vars || theme).palette.success.main,
    red: (theme.vars || theme).palette.error.main,
    yellow: (theme.vars || theme).palette.warning.main,
    gray: (theme.vars || theme).palette.text.secondary,
  };

  const iconColor = color ? colors[color] : null;
  return (
    <TreeItemLabel {...other} sx={{ display: 'flex', alignItems: 'center' }}>
      {iconColor && <DotIcon color={iconColor} />}
      <Typography
        className="labelText"
        variant="body2"
        sx={{ color: 'text.primary' }}
      >
        {children}
      </Typography>
    </TreeItemLabel>
  );
}

CustomLabel.propTypes = {
  children: PropTypes.node,
  color: PropTypes.oneOf(['blue', 'green', 'red', 'yellow', 'gray']),
  expandable: PropTypes.bool,
};

const CustomTreeItem = React.forwardRef(function CustomTreeItem(props, ref) {
  const { id, itemId, label, disabled, children, activeFilter, ...other } = props;

  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getLabelProps,
    getGroupTransitionProps,
    status,
    publicAPI,
  } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });

  const item = publicAPI.getItem(itemId);
  const color = item?.color;

  // Verificar se este item está ativo no filtro
  const isActiveFilter = React.useMemo(() => {
    if (!activeFilter) return false;

    const isCarrierLevel = !itemId.includes('.');

    if (isCarrierLevel) {
      // Nível transportadora
      const carrierName = item?.label?.split(' (')[0];
      return activeFilter.type === 'carrier' && activeFilter.carrierName === carrierName;
    } else {
      // Nível status
      const carrierItem = publicAPI.getItem(itemId.split('.')[0]);
      const carrierName = carrierItem?.label?.split(' (')[0];
      const statusKey = item?.status;

      return activeFilter.type === 'status' &&
        activeFilter.carrierName === carrierName &&
        activeFilter.statusKey === statusKey;
    }
  }, [activeFilter, itemId, item, publicAPI]);

  return (
    <TreeItemProvider id={id} itemId={itemId}>
      <TreeItemRoot {...getRootProps(other)}>
        <TreeItemContent
          {...getContentProps({
            className: clsx('content', {
              expanded: status.expanded,
              selected: status.selected,
              focused: status.focused,
              disabled: status.disabled,
            }),
            sx: isActiveFilter ? {
              backgroundColor: (theme) => theme.palette.primary.main + '20',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.primary.main + '30',
              }
            } : {}
          })}
        >
          {status.expandable && (
            <TreeItemIconContainer {...getIconContainerProps()}>
              <TreeItemIcon status={status} />
            </TreeItemIconContainer>
          )}

          <CustomLabel {...getLabelProps({ color })} />
        </TreeItemContent>
        {children && (
          <TransitionComponent
            {...getGroupTransitionProps({ className: 'groupTransition' })}
          />
        )}
      </TreeItemRoot>
    </TreeItemProvider>
  );
});

CustomTreeItem.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * If `true`, the item is disabled.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * The id attribute of the item. If not provided, it will be generated.
   */
  id: PropTypes.string,
  /**
   * The id of the item.
   * Must be unique.
   */
  itemId: PropTypes.string.isRequired,
  /**
   * The label of the item.
   */
  label: PropTypes.node,
};

export default function CustomizedTreeView({ trips = [], onFilterChange, activeFilter }) {
  // Processa os dados das trips
  const treeData = React.useMemo(() => processTripsData(trips), [trips]);

  // Se não há dados, mostra uma mensagem
  if (!trips || trips.length === 0) {
    return (
      <Card
        variant="outlined"
        sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}
      >
        <CardContent>
          <Typography component="h2" variant="subtitle2" sx={{ mb: 2 }}>
            Transportadoras por Status
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nenhuma viagem encontrada
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Determina quais itens devem estar expandidos por padrão
  const defaultExpandedItems = treeData.map(item => item.id);

  // Handler para cliques nos itens da árvore
  const handleItemClick = (event, itemId) => {
    if (!onFilterChange) return;

    const clickedItem = findItemById(treeData, itemId);
    if (!clickedItem) return;

    // Determinar se é transportadora (nível 1) ou status (nível 2)
    const isCarrierLevel = !itemId.includes('.');

    if (isCarrierLevel) {
      // Clique na transportadora
      const carrierName = clickedItem.label.split(' (')[0]; // Remove o count do label
      const newFilter = {
        type: 'carrier',
        carrierName: carrierName,
        label: carrierName
      };

      // Toggle: se já está filtrado pela mesma transportadora, remove o filtro
      if (activeFilter?.type === 'carrier' && activeFilter?.carrierName === carrierName) {
        onFilterChange(null);
      } else {
        onFilterChange(newFilter);
      }
    } else {
      // Clique no status
      const carrierItem = findItemById(treeData, itemId.split('.')[0]);
      const carrierName = carrierItem?.label.split(' (')[0];
      const statusLabel = clickedItem.label.split(' (')[0]; // Remove o count do label
      const statusKey = clickedItem.status;

      const newFilter = {
        type: 'status',
        carrierName: carrierName,
        statusKey: statusKey,
        statusLabel: statusLabel,
        label: `${carrierName} • ${statusLabel}`
      };

      // Toggle: se já está filtrado pelo mesmo status, remove o filtro
      if (activeFilter?.type === 'status' &&
        activeFilter?.carrierName === carrierName &&
        activeFilter?.statusKey === statusKey) {
        onFilterChange(null);
      } else {
        onFilterChange(newFilter);
      }
    }
  };

  // Função auxiliar para encontrar item por ID
  const findItemById = (items, id) => {
    for (const item of items) {
      if (item.id === id) {
        return item;
      }
      if (item.children) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  return (
    <Card
      variant="outlined"
      sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}
    >
      <CardContent>
        <Typography component="h2" variant="subtitle2">
          Transportadoras por Status
        </Typography>
        <RichTreeView
          items={treeData}
          aria-label="transportadoras-por-status"
          multiSelect
          defaultExpandedItems={defaultExpandedItems}
          onItemClick={handleItemClick}
          sx={{
            m: '0 -8px',
            pb: '8px',
            height: 'fit-content',
            flexGrow: 1,
            overflowY: 'auto',
          }}
          slots={{ item: CustomTreeItem }}
          slotProps={{
            item: { activeFilter }
          }}
        />
      </CardContent>
    </Card>
  );
}

CustomizedTreeView.propTypes = {
  /**
   * Array of trip objects to display in the tree view
   */
  trips: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    carrier_name: PropTypes.string,
    state: PropTypes.string,
    status: PropTypes.string,
  })),
  /**
   * Callback called when a filter is selected/deselected
   */
  onFilterChange: PropTypes.func,
  /**
   * Currently active filter
   */
  activeFilter: PropTypes.shape({
    type: PropTypes.oneOf(['carrier', 'status']),
    carrierName: PropTypes.string,
    statusKey: PropTypes.string,
    statusLabel: PropTypes.string,
    label: PropTypes.string,
  }),
};
